
import { computed, defineComponent, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// module components
import StepCompany from '@/modules/CreateOrderModule/components/StepCompany/index.vue'
import StepCity from '@/modules/CreateOrderModule/components/StepCity/index.vue'
import StepSenders from '@/modules/CreateOrderModule/components/StepSenders/index.vue'

import StepReceiversExpress from '@/modules/CreateOrderModule/components/StepReceiversExpress/index.vue'
import StepTariffs from '@/modules/CreateOrderModule/components/StepTariffs/index.vue'
import StepProducts from '@/modules/CreateOrderModule/components/StepProducts/index.vue'
import ProductsList from '@/modules/CreateOrderModule/components/ProductsList/index.vue'
import StepAdditionalData from '@/modules/CreateOrderModule/components/StepAdditionalData/index.vue'

// types
import { ExpressAdditionalDataT } from '@/core/types/express.types'
import { ReceiverExpressT } from '@/core/types/receivers.types'
import { SenderT } from '@/core/types/senders.types'

import {
    DeliveryExpressOrderPayloadT,
    DeliveryPointT,
    DeliveryProductT,
} from '@/core/types/delivery.types'

// constants
import { SPARK_VERIFICATION_ID, KASPI_VERIFICATION_ID } from '@/core/constants/express.constants'

// composable
import useNotifications from '@/common/composable/useNotifications'

// realisations
import { delivery } from '@/core/realisations/delivery.realisation'
import { useRouter } from 'vue-router'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        //
        'step-company': StepCompany,
        'step-city': StepCity,
        'step-senders': StepSenders,
        StepReceiversExpress,
        'step-tariffs': StepTariffs,
        'step-products': StepProducts,
        'products-list': ProductsList,
        'step-additional-data': StepAdditionalData,
    },
    setup() {
        const notifications = useNotifications()
        const router = useRouter()

        const state = reactive({
            company_id: null as number | null,
            // city
            city_id: null as number | null,
            // sender
            sender: null as SenderT | null,
            // receiver
            receivers: [] as ReceiverExpressT[],
            // points
            points: [] as DeliveryPointT[],
            // tariff
            transport_type: null as string | null,
            tariff_price: null as number | null,
            //
            is_spark_verification: false,
            is_kaspi_verification: false,
            //
            is_additional_data_setted: false,
            // create order
            is_creating_order: false,
            is_order_created: false,
            is_fastest: false,
        })

        // companies actions
        const onCompanySubmit = (company_id: number) => {
            state.company_id = company_id
        }

        const onCompanyReset = () => {
            state.company_id = null
            state.city_id = null
            state.sender = null
            state.receivers = []
            state.transport_type = null
            state.tariff_price = null
            state.points = []
        }

        // city actions
        const onCitySubmit = (city_id: number) => {
            state.city_id = city_id
        }

        const onCityReset = () => {
            state.city_id = null
            state.sender = null
            state.receivers = []
            state.transport_type = null
            state.tariff_price = null
            state.points = []
        }

        // senders actions
        const onSenderSubmit = (sender: SenderT) => {
            if (!sender.longitude || !sender.latitude) {
                return
            }

            state.sender = sender
            state.points.unshift({ longitude: sender.longitude, latitude: sender.latitude })
        }

        const onSenderReset = () => {
            state.sender = null
            state.transport_type = null
            state.tariff_price = null
            state.points.splice(0, 1)
        }

        // receiver actions
        const onReceiverSubmit = (receiver: ReceiverExpressT) => {
            if (!receiver.longitude || !receiver.latitude) {
                return
            }

            state.receivers.push({
                ...receiver,
                items: [],
                tasks: [],
                amount_to_accept_payment: 0,
            })
            state.points.push({ longitude: receiver.longitude, latitude: receiver.latitude })
        }

        const onReceiverReset = (index: number) => {
            state.receivers.splice(index, 1)
            state.transport_type = null
            state.tariff_price = null
            state.points.splice(index, 1)
        }

        // tariff actions
        const onTariffSubmit = ({
            transport,
            price,
            is_fastest,
        }: {
            transport: string
            price: number
            is_fastest: boolean
        }) => {
            state.transport_type = transport
            state.tariff_price = price
            state.is_fastest = is_fastest
        }

        const onTariffReset = () => {
            state.transport_type = null
            state.tariff_price = null
        }

        // products actions
        const onAddProduct = ({ product, index }: { product: DeliveryProductT; index: number }) => {
            state.receivers[index].items.push(product)
        }

        const onRemoveProduct = ({
            product_index,
            receiver_idx,
        }: {
            product_index: number
            receiver_idx: number
        }) => {
            state.receivers[receiver_idx].items.splice(product_index, 1)
        }

        // additional data actions
        const onAdditionalDataSubmit = (additional_data: ExpressAdditionalDataT) => {
            const {
                extra_info,
                tasks,
                verification_invoice_number,
                is_spark_verification,
                is_kaspi_verification,
                index,
            } = additional_data

            state.receivers[index].extra_info = extra_info
            state.receivers[index].verification_invoice_number = verification_invoice_number
            state.receivers[index].tasks = tasks

            if (is_kaspi_verification) {
                state.receivers[index].verification_type = KASPI_VERIFICATION_ID
            }
            if (is_spark_verification) {
                state.receivers[index].verification_type = SPARK_VERIFICATION_ID
            }

            state.is_additional_data_setted = true
        }

        const onAdditionalDataReset = (index: number) => {
            state.is_spark_verification = false
            state.is_kaspi_verification = false
            state.receivers[index].tasks = []

            state.is_additional_data_setted = false
        }

        const getProductName = (): string => {
            let product_name = ''

            state.receivers.forEach((receiver) => {
                receiver.items.forEach((item, index) => {
                    if (index !== receiver.items.length - 1) {
                        product_name += `${item.title};`
                    } else {
                        product_name += item.title
                    }
                })
            })

            return product_name
        }

        const createPayload = (): DeliveryExpressOrderPayloadT | void => {
            if (
                !state.city_id ||
                !state.transport_type ||
                !state.sender ||
                !state.receivers.length
            ) {
                return
            }

            const payload: DeliveryExpressOrderPayloadT = {
                city_id: state.city_id,
                transport_type: state.transport_type,
                product_name: getProductName(),
                sender: state.sender,
                receivers: state.receivers,
                origin: 1,
                is_fastest: state.is_fastest,
            }

            return payload
        }

        const createExpressOrder = async () => {
            if (!isCanCreateOrder.value) return

            try {
                state.is_creating_order = true

                const payload = createPayload()
                if (!payload) return

                await delivery.createExpressOrder(payload)

                state.is_order_created = true
                notifications.notify({ title: 'Заказ создан', type: 'success' })
                await router.push('/express')
            } catch (error) {
                notifications.notifyError({ error, title: 'При создании заказа произошла ошибка' })
            } finally {
                state.is_creating_order = false
            }
        }

        // computeds
        const isCompanySelected = computed(() => Boolean(state.company_id))
        const isCitySelected = computed(() => Boolean(state.city_id))
        const isSenderSelected = computed(() => Boolean(state.sender))
        const isReceiverSelected = computed(() => Boolean(state.receivers.length))
        const isTariffSelected = computed(() => Boolean(state.transport_type))
        const isHaveProducts = computed(() =>
            state.receivers.some((receiver) => receiver.items.length)
        )
        const isAdditionalDataSetted = computed(() => state.is_additional_data_setted)

        const isCanCreateOrder = computed(
            () =>
                isCompanySelected.value &&
                isCitySelected.value &&
                isSenderSelected.value &&
                isReceiverSelected.value &&
                isTariffSelected.value &&
                isHaveProducts.value &&
                isAdditionalDataSetted.value &&
                !state.is_order_created
        )

        const isActionsDisabled = computed(() => state.is_creating_order || state.is_order_created)

        return {
            state,
            // company
            onCompanySubmit,
            onCompanyReset,
            // city
            onCitySubmit,
            onCityReset,
            // sender
            onSenderSubmit,
            onSenderReset,
            // receiver
            onReceiverSubmit,
            onReceiverReset,
            // tariff
            onTariffSubmit,
            onTariffReset,
            // products
            onAddProduct,
            onRemoveProduct,
            //
            onAdditionalDataSubmit,
            onAdditionalDataReset,
            //
            createExpressOrder,
            // computeds
            isCompanySelected,
            isCitySelected,
            isSenderSelected,
            isReceiverSelected,
            isAdditionalDataSetted,
            isCanCreateOrder,
            isActionsDisabled,
        }
    },
})
